import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import axios from 'axios';
import { SYMFONY_API_BASE_URL } from '@config/apiConfig';
import { captureException } from '@sentry/react';
import SectionContainer from '@components/UI/Section/components/SectionContainer';
import SectionHead from '@components/UI/Section/components/SectionHead';
import { SectionHeading } from '@components/UI/Section/components/SectionHeading';
import SectionSubheading from '@components/UI/Section/components/SectionSubheading';
import loadable from '@loadable/component';
import useUserGamesListener from '@components/Hooks/useUserGamesListener';
import ConfirmModal from '@components/Modals/ConfirmModal';
import GenericModal from '@components/Modals/GenericModal';
import { useUpdateUserState, useUserState } from '@context/UserContext';
import getGamePalette from '@helpers/getGamePalette';
import { useLocalAppContext } from '@context/LocalAppContext';
import useTrackEvent from '@hooks/useTrackEvent';
import CrossCloseIcon from '@components/UI/CrossCloseIcon';
import GameAccountsBody from './GameAccountsBody';
import WarningIcon from '@components/Icons/WarningIcon';
import CoreButton from '@components/Buttons/CoreButton';
import { useFeature } from 'flagged';
const Icon = loadable(() => import('@components/Icons/Icon'));
const useStyles = makeStyles()((theme, { selectedPalette }) => ({
    deleteIcon: {
        height: '86px',
        width: '86px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette[selectedPalette].main,
        '& svg': {
            height: '40px',
            fill: theme.palette.common.white,
        },
    },
    warningModalContent: {
        padding: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(3),
        textAlign: 'center',
    },
    warningHeading: {
        fontSize: '20px',
        fontWeight: 700,
        margin: 0,
        color: theme.palette.text.primary,
    },
    warningDescription: {
        fontSize: '14px',
        margin: 0,
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
    },
    warningButton: {
        color: theme.palette.grey[600],
        border: `2px solid ${theme.palette.grey[100]}`,
        fontWeight: 'bold',
        '&:hover': {
            color: theme.palette.warning.main,
        },
    },
    closeBtn: {
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        fontSize: '26px',
    },
}));
const GameAccounts = ({ className, isCurrentUser, userHandle }) => {
    var _a, _b, _c;
    const { loading, allUserGameRegions, triggerReload: reloadUserGames } = useUserGamesListener(userHandle);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [showTeamWarning, setShowTeamWarning] = useState(false);
    const gameColor = getGamePalette((_a = confirmDelete === null || confirmDelete === void 0 ? void 0 : confirmDelete.game.gameKey) !== null && _a !== void 0 ? _a : 'fortnite');
    const { classes, cx } = useStyles({ selectedPalette: gameColor });
    const { userDetails } = useUserState();
    const { refresh: refreshUserState } = useUpdateUserState();
    const allData = allUserGameRegions();
    const { openModal } = useLocalAppContext();
    const trackEvent = useTrackEvent();
    const isWithdrawalOnlyEnabled = !!useFeature('semisonic_phase_2');
    const onConfirmDelete = async () => {
        try {
            setDeleting(true);
            if ((confirmDelete === null || confirmDelete === void 0 ? void 0 : confirmDelete.game.gameKey) === 'battlefield-6' && !isWithdrawalOnlyEnabled) {
                // Check if user is in a team for this game
                const checkTeamResponse = await axios.get(`${process.env.REACT_REPEAT_TEAM_SERVICE_URL}/team/v1/team/check-game-account`, {
                    params: {},
                });
                const { isInTeam } = checkTeamResponse.data;
                if (isInTeam) {
                    setShowTeamWarning(true);
                    setDeleting(false);
                    return;
                }
            }
            await axios.delete(`${SYMFONY_API_BASE_URL}/api/user_games/region/${confirmDelete === null || confirmDelete === void 0 ? void 0 : confirmDelete.id}`);
            setDeleting(false);
            setConfirmDelete(null);
            refreshUserState();
            reloadUserGames();
        }
        catch (e) {
            captureException(e, {
                extra: {
                    request: 'region_delete',
                },
            });
            setDeleting(false);
        }
    };
    const onDeleteAccountClick = (ugr) => {
        if (isCurrentUser) {
            setConfirmDelete(ugr);
        }
    };
    const handleShowAddGameModal = (addGameButtonLabel) => {
        openModal({ type: 'add-game', onClose: () => { } });
        trackEvent({
            type: 'game_linking',
            data: {
                eventName: 'initiate game linking',
                label: addGameButtonLabel,
                moduleName: 'game linking',
                position: 1, // position 0 is the sidebar add game button
            },
        });
    };
    const handleClose = () => {
        setShowTeamWarning(false);
        setConfirmDelete(null);
    };
    return (_jsxs(SectionContainer, { className: cx(className), children: [_jsxs(SectionHead, { children: [_jsx(SectionHeading, { children: isCurrentUser ? 'Game IDs' : 'My Games' }), _jsx(SectionSubheading, { children: "Game accounts I've connected" })] }), _jsx(GameAccountsBody, { loading: loading, userGames: allData, onConnectAccountsClick: handleShowAddGameModal, onDeleteAccountClick: onDeleteAccountClick, isCurrentUser: isCurrentUser, userHandle: userHandle }), isCurrentUser && (_jsxs(_Fragment, { children: [_jsx(ConfirmModal, { show: confirmDelete != null && !showTeamWarning, icon: _jsx("div", { className: classes.deleteIcon, children: _jsx(Icon, { icon: (_b = confirmDelete === null || confirmDelete === void 0 ? void 0 : confirmDelete.game.gameKey) !== null && _b !== void 0 ? _b : '' }) }), heading: _jsx("span", { "data-notranslate": true, children: `Delete ${(_c = confirmDelete === null || confirmDelete === void 0 ? void 0 : confirmDelete.handle) !== null && _c !== void 0 ? _c : ''}?` }), description: _jsxs(_Fragment, { children: ["By clicking delete, you will be removed from all ongoing tournaments using this Game ID (other games will not effected).", _jsx("br", {}), _jsx("br", {}), " You will also need to wait 30 days before you can connect a different Game ID in the same region.", (userDetails === null || userDetails === void 0 ? void 0 : userDetails.isSuspended) ? (_jsxs("strong", { children: [_jsx("br", {}), _jsx("br", {}), " This Game ID has been suspended and will not be able to be added to other Repeat accounts.", _jsx("br", {}), _jsx("br", {}), " If you are attempting to circumnavigate your recent suspension by adding this Game ID to another accounts you risk both accounts being banned."] })) : null] }), loading: deleting, confirmText: "Delete", confirmColor: gameColor, handleConfirm: onConfirmDelete, handleClose: () => setConfirmDelete(null) }), _jsx(GenericModal, { show: showTeamWarning, onClose: handleClose, size: "small", children: _jsxs("div", { className: classes.warningModalContent, children: [_jsx(CrossCloseIcon, { className: classes.closeBtn, action: handleClose }), _jsx("div", { children: _jsx(WarningIcon, {}) }), _jsx("h2", { className: classes.warningHeading, children: "Leave Your Team Before Removal" }), _jsx("p", { className: classes.warningDescription, children: "You're currently part of one or more teams. To unlink your EA account, please leave your teams first." }), _jsx(CoreButton, { appearance: "outline", size: "md", variant: "info", className: classes.warningButton, onClick: () => {
                                        setShowTeamWarning(false);
                                        setConfirmDelete(null);
                                    }, block: true, children: "Dismiss" })] }) })] }))] }));
};
export default GameAccounts;
